/**
 * The one piece of match-clock arithmetic the phone and the server both have to agree on.
 *
 * Authority: `um-app/13-match-debrief.md` §3.2 (*"`Full Time` is approximated, and this is
 * stated rather than implied"*), and the Technical Delegate's ruling **R-D6** of 11 September
 * 2026 — the Consolidated Daily UM Debrief Report builds *"thirty minutes after the last match
 * of the day ends"*.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY IT LIVES IN THE SHARED PACKAGE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Run 73 put {@link MATCH_LENGTH_MINUTES} in `apps/web/src/pages/um/matchDebrief.ts`, because
 * the only thing that needed it was a control on a phone. R-D6 gives the SERVER the same
 * arithmetic: the nightly build time is the last fixture's scheduled start plus this number
 * plus the configured delay. Two constants — one on each side — would be one number with two
 * homes, and the day somebody tuned the phone's and not the server's, the `Debrief` control
 * would appear at a time the report was not built from.
 *
 * So the truth moved here, where both halves of the application already import from, and
 * `matchDebrief.ts` re-exports it unchanged so nothing that already named it has to move.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A DELIBERATE APPROXIMATION, AND THE HONEST ONE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * **This system holds no Full Time signal.** The live clock relay of 00-overview §7.3 does not
 * exist, which is the same absence that stamps every captured minute `minuteProvisional`, and a
 * fixture carries a date and a scheduled kick-off and nothing about what happened during it. So
 * *"has finished"* is the scheduled start plus the wall-clock length of a hockey match — four
 * fifteen-minute quarters, two two-minute breaks, a ten-minute half, stoppages and a possible
 * shoot-out — rounded to a round number that is late enough not to call half time full time and
 * early enough to be there when the managers walk off the pitch.
 */
/**
 * How long after its scheduled start a match is treated as finished — **100 minutes**.
 *
 * Read by the phone's `Debrief` entry rule (spec 13 §3.2) and by the server's nightly build
 * time (R-D6). One number, one definition, two readers.
 */
export const MATCH_LENGTH_MINUTES = 100;
//# sourceMappingURL=match-clock.js.map